<template>
	<div>
		<h3>Simple Mode</h3>
		<b-img src="/images/importer/module-materials/materials-simple.jpg"></b-img>
		<p>
			The Materials and Textures Module is for setting up support for customizable patterns and skins for your character, if desired. Most custom characters will probably just have a single 
			material or set of materials with no alternate skin textures, and can set this module to "Simple" mode.
		</p>
        
		<h3 class="mt-5">Customizable Mode</h3>
		<b-img src="/images/importer/module-materials/character-materials-component.jpg"></b-img>
		<p>
			To get started with Customizable mode, click the "Initialize Material Data" button. This will generate a list of Material Data elements here for each of the materials on each of the
			mesh renderers that your character has.
		</p>
        
		<h4 class="mt-5">Material Data</h4>
		<b-img src="/images/importer/module-materials/material-data-section.jpg"></b-img>
		<p>
			Each material on your character that you want to be customizable should have a Material Data entry. After clicking the Initialize button you may have some extra Material Data entries in
			this list for materials that you don't want to change - you can just delete them from the list.
		</p>
        
		<p>
			<span class="font-weight-bold">Body Part Name:</span> The name to use for the material. This will appear in the Skin Edit mode in-game as a heading for editing this material's properties.
		</p>
        
		<p>
			<span class="font-weight-bold">Material Options:</span> This list is used to allow you to define alternate materials that can be swapped out for this material in the Skin Edit mode. This
			is useful if you want to allow for normal maps, occlusion maps, etc. to be swapped. Note that all materials in the Material Options list are expected to use the same shader.
		</p>
        
		<p>
			<span class="font-weight-bold">Main Texture Property Name:</span> The shader's property name for its diffuse texture. For Unity Standard shaders this can stay as the default value _MainTex.
		</p>
        
		<p>
			<span class="font-weight-bold">Properties:</span> Define which material shader properties you want to be editable by users in the Skin Edit mode. By default this list is populated with some of
			the properties of the Standard shader, so if your material's shader uses different property names then you'll need to modify them. You can also remove any properties from this list if you
			don't want them to appear in the Skin Edit mode.
		</p>

		<p>
			<span class="font-weight-bold">Is Paintable:</span> When selected, this will generate some components on the mesh in-game that will allow it to be painted. Currently, the only paintable
			property is "Wetness", which paints a white color on a texture channel when penetrating a penetrable or when the VR player moves their lips over the mesh. When mapped to a smoothness
			texture this simulates a wet surface.
		</p>
        
		<p>
			<span class="font-weight-bold">Paintable Wetness Property Name:</span> If "Is Paintable" is selected, this is the property name on the skinned mesh renderer's material shader that will
			be painted on when it is appropriate to paint wetness. This should be the name of a Texture2D property on the shader. Note that this functionality is not supported on a regular Standard
			shader - the default value is _PaintableSmoothness, which is the property name used by CharacterShader.
		</p>

        
		<h4 class="mt-5">Pattern Types</h4>
		<b-img src="/images/importer/module-materials/pattern-types-section.jpg"></b-img>
		<p>
			A Pattern Type is a way to group your Patterns defined for your character, if there are multiple parts that should be separately customizable. The usual case for this would be having separate
			materials on the main body and the cock, or the body and wings, and you want users to be able to mix and match these patterns. Most characters will just need one Pattern Type defined.
		</p>
        

		<h4 class="mt-5">Patterns</h4>
		<b-img src="/images/importer/module-materials/patterns-section.jpg"></b-img>
		<p>
			This section is for defining the editable Material Data elements that each pattern should affect, and any texture layers that those materials should use for their diffuse textures. Textures
			used for these diffuse texture layers should use alpha values to define which parts of the texture they should color, and ideally should be white/grayscale so they can be tinted to any color
			by users in the Skin Edit mode. In the Texture Layers list, textures will be applied in order from first in the list at the bottom, to last in the list on top. See the Feral example scene 
			for an example of the texture layers and pattern setup.
		</p>
        

		<h4 class="mt-5">Skins</h4>
		<b-img src="/images/importer/module-materials/skins-section.jpg"></b-img>
		<p>
			This section is for defining built-in skins for your character. A Skin is just a set of colors to assign to each of the texture layers for each pattern, and colors or number values to assign
			to each of the Material Properties listed in each Material Data in that pattern. This is essentially the same as filling out the fields in the Skin Edit mode in-game, with a few additional fields.
		</p>

		<p>
			<span class="font-weight-bold">Is Default:</span> Select this on a skin to make it the default selection when the character species is selected in the Character Menu. Only one skin should have
			this box checked.
		</p>
        
		<p>
			<span class="font-weight-bold">Compiled Texture:</span> For pattern elements that have Texture Layers defined, this file should be the end result of combining all of the diffuse texture layers
			with the colors defined by this skin.
		</p>
        
		<p>
			<span class="font-weight-bold">Load Skin From JSON:</span> This button allows for a much quicker/easier workflow for setting up built-in skins for your character, rather than filling out all the
			fields here in the importer. Whenever you make a custom skin in-game, a set of files are generated in the game's persistent storage location 
			(C:/Users/(your username)/AppData/LocalLow/Softscale/FarmD/PlayerData/Skins/) - within this folder is a set of folders for each character, and within those a set of folders for each custom skin.
			In each skin folder is a .json file and the baked diffuse textures for any patterns that use texture layers - these baked textures are what should be placed in the "Compiled Texture" field for
			the corresponding pattern. The json file is what this button expects to work with, and will add a new skin here in the importer with the values that were defined in-game. So, an easy workflow
			for getting your skins set up would be to make a temporary skin and just leave all of its fields at their defaults, then export the .fdc and load it into the game and use the in-game Skin Edit
			mode to define your skin's properties and colors. That way you can see how it will look in-game, and generate the compiled textures when you save it, copy those textures into your importer project
			in the character's Input Directory, use this Load Skin button to add the skin, and delete the initial temporary skin.
		</p>


		<h4>Eye Types</h4>
		<b-img src="/images/importer/module-materials/eye-type-module.jpg"></b-img>
		<p>
			If your character has customizable eye types, check the "Has Eye Types" box and fill out the section that appears.
		</p>
        
		<b-img src="/images/importer/module-materials/eye-types.jpg"></b-img>
		<p>
			<span class="font-weight-bold">Eye Type Names:</span> These are the names of the supported eye types, which will appear in the Character Menu dropdown. Eye textures are handled a little 
			differently than body textures - since only one eye type is visible at once, but the color of that eye's layer when set in the Skin Edit mode should apply to all eye types, the eye
			texture is expected to contain all eye types in one texture so they can all be tinted with that color at the time the skin is saved. The number and order of items in this Eye Type 
			Names array determines how this eye texture should be laid out - it assumes that the texture will be square, so 2-4 eye types will fit in a 2x2 grid, 5-9 eye types will fit in a 3x3 grid, etc.
			The eye types will be ordered from left to right, top to bottom. You can see from the image above that two eye types are available, Round and Slit - both were placed into a larger texture 
			in this order, and this is what is used for the "iris" texture layer. The scaling and offset of the eye's UVs will then be modified in-game in order to change which part of the texture 
			is used to display the "current" eye type.
		</p>
        
		<p>
			<span class="font-weight-bold">Eye Renderers:</span> Select the mesh renderers that will be affected by the eye types. It is expected that the eyes be separate meshes from any other 
			renderers such as the body, since their UV coordinates will be affected by the Eye Types system.
		</p>
	</div>
</template>

<script>
export default {
		name: 'MaterialsAndTexturesModule',
}
</script>
